import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type { PendingRecordDto } from '../services/trungTamApi';
import { getPendingRecordDetailApi, bulkTransferPendingApi, redetectPendingApi } from '../services/trungTamApi';
import type { BatchItem, CccdScanResult, ExtraImage, TtCourse } from '../types';

type Props = {
  records: PendingRecordDto[];
  isProcessing: boolean;
  doneCount: number;
  errorCount: number;
  pendingCount: number;
  /** Open the detail modal with one or more items. Called by the row
   *  click handler (single item) AND the "Xem & lưu (N)" button
   *  (all done records). */
  onReview: (items: BatchItem[]) => void;
  onClear: () => void;
  onRemove: (id: number) => void;
  onRetry: (id: number) => void;
  /** Courses the operator can send residual pending rows to. Parent
   *  filters to open courses; we further filter by hang match at render
   *  time since pending rows come from the currently selected course. */
  courses: TtCourse[];
  currentCourseId: number | null;
  currentCourseHang: string;
  /** Called after a successful bulk transfer — parent refreshes queue +
   *  record list so UI stays in sync with DB. */
  onTransferred: () => void | Promise<void>;
};

/** Add data-URL prefix if missing — the batch review UI renders base64
 *  as <img src="..."> which requires a full data URL, but DB storage is
 *  bare base64. */
const asDataUrl = (b64: string | null | undefined): string | null => {
  if (!b64) return null;
  if (b64.startsWith('data:')) return b64;
  return `data:image/jpeg;base64,${b64}`;
};

/** Convert a PendingRecordDto (DB row) into a CccdScanResult the batch review UI expects. */
const toScanResult = (p: PendingRecordDto): CccdScanResult => {
  let extras: ExtraImage[] = [];
  if (p.extraImagesJson) {
    try {
      const parsed = JSON.parse(p.extraImagesJson);
      if (Array.isArray(parsed)) extras = parsed;
    } catch { /* ignore malformed JSON */ }
  }
  return {
    soCCCD: p.soCCCD || '',
    soCMT: p.soCMT || '',
    hoTen: p.hoTen || '',
    ngaySinh: p.ngaySinh || '',
    gioiTinh: p.gioiTinh || '',
    diaChi: p.diaChi || '',
    diaChiMoi: p.diaChiCap2 || '',
    ngayCap: p.ngayCap || '',
    avatarBase64: asDataUrl(p.avatarBase64),
    sourceImageBase64: asDataUrl(p.sourceImageBase64),
    detectedMethod: p.scanMethod || null,
    // Use per-doc source DIRECTLY — do NOT fall back to record-level
    // scanMethod. Reason: scanMethod collapses to 'qr' when ANY doc has
    // a QR hit, so if CCCD=QR + GPLX=OCR, scanMethod='qr' and the
    // fallback would make GPLX appear as QR too (wrong). Leaving the
    // field null lets the badge logic either render only the doc that
    // HAS a source, or omit the source chip entirely when legacy records
    // never stored the per-doc column.
    cccdSource: p.cccdSource ?? null,
    gplxSource: p.gplxSource ?? null,
    extraImages: extras,
    soGPLX: p.soGPLX || '',
    hangGPLX: p.hangGPLX || '',
    hangGPLXMoi: p.hangGPLXMoi || '',
    ngayCapGPLX: p.ngayCapGPLX || '',
    noiCapGPLX: '',
    donViCapGPLX: '',
    ngayTrungTuyenGPLX: p.ngayTrungTuyenGPLX || '',
    ngayHetHanGPLX: p.ngayHetHanGPLX || '',
    maDvhc: p.maDvhc || '',
    dvhcTenDayDu: p.dvhcTenDayDu || undefined,
    portraitBase64: asDataUrl(p.portraitBase64),
    portraitOriginalBase64: asDataUrl(p.portraitOriginalBase64),
    portraitAiVariantsJson: p.portraitAiVariantsJson || null,
    faceMatchScore: p.faceMatchScore ?? null,
    teacherId: p.teacherId ?? null,
    teacherFullName: p.teacher?.username ?? null,
  };
};

const PendingRecordsTab: React.FC<Props> = ({
  records, doneCount, errorCount, pendingCount,
  onReview, onClear, onRemove, onRetry,
  courses, currentCourseId, currentCourseHang, onTransferred,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState<number | null>(null);
  const [submittingTransfer, setSubmittingTransfer] = useState(false);
  // Track which row is currently being re-detected so we can show a
  // per-row spinner without freezing the whole table.
  const [redetectingId, setRedetectingId] = useState<number | null>(null);

  const eligibleTargets = useMemo(
    () => courses.filter(c => c.status === 'open' && c.id !== currentCourseId && c.hang === currentCourseHang),
    [courses, currentCourseId, currentCourseHang],
  );

  const selectableIds = useMemo(() => records.map(r => r.id), [records]);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));

  const toggleOne = (id: number) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(selectableIds));
  };

  const handleReviewClick = async () => {
    const doneRecords = records.filter(r => r.status === 'done');
    if (doneRecords.length === 0) return;

    setLoading(true);
    try {
      // Fetch full detail (including base64) for each done record so the
      // batch review UI can render them without a second round-trip.
      const details = await Promise.all(
        doneRecords.map(r => getPendingRecordDetailApi(r.id).catch(() => null)),
      );
      const items: BatchItem[] = details
        .filter((d): d is NonNullable<typeof d> => !!d && d.EC === 0 && !!d.DT)
        .map(d => {
          const p = d.DT!;
          const result = toScanResult(p);
          return {
            fileName: result.hoTen || result.soCCCD || `Record-${p.id}`,
            preview: result.sourceImageBase64
              ? `data:image/jpeg;base64,${result.sourceImageBase64}`
              : '',
            status: 'done' as const,
            result,
            pendingRecordId: p.id,
          };
        });

      if (items.length === 0) {
        toast.warning('Không tải được chi tiết hồ sơ');
        return;
      }
      onReview(items);
    } catch {
      toast.error('Lỗi khi tải chi tiết hồ sơ');
    } finally {
      setLoading(false);
    }
  };

  const handleClearClick = () => {
    if (!window.confirm('Hủy bỏ toàn bộ hồ sơ chờ lưu? Dữ liệu sẽ bị xóa khỏi database.')) return;
    onClear();
  };

  const handleRemoveClick = (id: number) => {
    if (!window.confirm('Hủy hồ sơ này? Dữ liệu sẽ bị xóa.')) return;
    onRemove(id);
  };

  const handleTransferSubmit = async () => {
    if (!transferTarget || selectedIds.size === 0) return;
    setSubmittingTransfer(true);
    try {
      const res = await bulkTransferPendingApi(Array.from(selectedIds), transferTarget);
      if (res.EC !== 0) {
        toast.error(res.EM || 'Lỗi chuyển hồ sơ');
        return;
      }
      const dt = res.DT;
      if (dt) {
        toast.success(
          dt.skipped.length > 0
            ? `Đã chuyển ${dt.transferred} sang "${dt.targetCourseName}" (bỏ qua ${dt.skipped.length})`
            : `Đã chuyển ${dt.transferred} sang "${dt.targetCourseName}"`
        );
        dt.skipped.forEach(sk => toast.warn(`Bỏ qua ${sk.soCCCD || `#${sk.id}`}: ${sk.reason}`));
      }
      setSelectedIds(new Set());
      setTransferOpen(false);
      setTransferTarget(null);
      await onTransferred();
    } catch {
      toast.error('Lỗi chuyển hồ sơ');
    } finally {
      setSubmittingTransfer(false);
    }
  };

  /** Re-run OCR on stored images. Used when the first detect attempt
   *  failed (eg Gemini 503 high-demand) so the operator doesn't have to
   *  re-upload photos. Backend only fills NULL fields — anything the
   *  operator typed by hand is preserved. */
  const handleRedetectClick = async (recordId: number) => {
    if (redetectingId !== null) return;
    setRedetectingId(recordId);
    try {
      const resp = await redetectPendingApi(recordId);
      if (resp.EC !== 0 || !resp.DT) {
        toast.error(resp.EM || 'Detect lại thất bại');
        return;
      }
      const dt = resp.DT;
      if (dt.cccdEmpty && dt.gplxEmpty) {
        toast.warning(resp.EM || 'Detect lại không thu được dữ liệu — thử lại sau ít phút');
      } else {
        const parts: string[] = [];
        if (!dt.cccdEmpty) parts.push('CCCD');
        if (!dt.gplxEmpty) parts.push('GPLX');
        toast.success(`Đã detect lại ${parts.join(' + ')}`);
      }
      // Refresh parent so the updated row appears immediately.
      await onTransferred();
    } catch (err) {
      const e = err as { response?: { data?: { EM?: string } }; message?: string };
      toast.error(e?.response?.data?.EM || e?.message || 'Lỗi khi detect lại');
    } finally {
      setRedetectingId(null);
    }
  };

  const handleRowClick = async (recordId: number) => {
    setLoading(true);
    try {
      const resp = await getPendingRecordDetailApi(recordId);
      if (resp.EC === 0 && resp.DT) {
        const p = resp.DT;
        const result = toScanResult(p);
        const item: BatchItem = {
          fileName: result.hoTen || result.soCCCD || `Record-${p.id}`,
          preview: result.sourceImageBase64 || '',
          status: 'done',
          result,
          pendingRecordId: p.id,
        };
        onReview([item]);
      } else {
        toast.error(resp.EM || 'Không tải được chi tiết hồ sơ');
      }
    } catch {
      toast.error('Lỗi khi tải chi tiết hồ sơ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tt-pending-tab">
      {loading && (
        <div
          className="tt-pending-tab__loading-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            zIndex: 10000,
            backdropFilter: 'blur(2px)',
          }}
        >
          <span className="tt-spinner" style={{ width: 40, height: 40, borderWidth: 4 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: '#334' }}>Đang tải hồ sơ…</span>
        </div>
      )}
      <div className="tt-pending-tab__header">
        <h2 className="tt-pending-tab__title">
          <i className="material-icons">pending_actions</i>
          Chờ lưu hồ sơ
        </h2>
        <div className="tt-pending-tab__summary">
          <span className="tt-pending-tab__stat">
            <i className="material-icons">inventory_2</i>
            Tổng: <strong>{records.length}</strong>
          </span>
          {pendingCount > 0 && (
            <span className="tt-pending-tab__stat tt-pending-tab__stat--detecting">
              <span className="tt-spinner tt-spinner--sm" />
              Đang xử lý: <strong>{pendingCount}</strong>
            </span>
          )}
          {doneCount > 0 && (
            <span className="tt-pending-tab__stat tt-pending-tab__stat--done">
              <i className="material-icons">check_circle</i>
              Xong: <strong>{doneCount}</strong>
            </span>
          )}
          {errorCount > 0 && (
            <span className="tt-pending-tab__stat tt-pending-tab__stat--error">
              <i className="material-icons">error</i>
              Lỗi: <strong>{errorCount}</strong>
            </span>
          )}
        </div>
      </div>

      <div className="tt-pending-tab__actions">
        <button
          type="button"
          className="tt-btn tt-btn--success"
          onClick={handleReviewClick}
          disabled={doneCount === 0 || loading}
        >
          {loading ? <span className="tt-spinner tt-spinner--sm" /> : <i className="material-icons">fact_check</i>}
          Xem &amp; lưu ({doneCount})
        </button>
        <button
          type="button"
          className="tt-btn tt-btn--outline"
          onClick={() => setTransferOpen(true)}
          disabled={selectedIds.size === 0 || eligibleTargets.length === 0}
          title={
            eligibleTargets.length === 0
              ? `Không có khóa nào đang mở cùng hạng ${currentCourseHang}`
              : `Chuyển ${selectedIds.size} hồ sơ sang khóa khác`
          }
        >
          <i className="material-icons">swap_horiz</i>
          Chuyển khóa ({selectedIds.size})
        </button>
        <button
          type="button"
          className="tt-btn tt-btn--outline tt-btn--danger"
          onClick={handleClearClick}
          disabled={records.length === 0}
        >
          <i className="material-icons">cancel</i>
          Hủy bỏ tất cả
        </button>
      </div>

      {records.length === 0 ? (
        <div className="tt-pending-tab__empty">
          <i className="material-icons">inbox</i>
          <p>Chưa có hồ sơ nào trong hàng đợi</p>
          <p className="tt-pending-tab__empty-hint">
            Chuyển sang tab "Nhập hồ sơ trực tiếp" và bấm "+ Thêm hồ sơ mới" để bắt đầu
          </p>
        </div>
      ) : (
        <div className="tt-pending-list">
          <div className="tt-pending-list__header">
            <span className="tt-pending-list__check">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                title={allSelected ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
              />
            </span>
            <span>#</span>
            <span>Học viên</span>
            <span>Số CCCD</span>
            <span>Ảnh</span>
            <span>Nguồn</span>
            <span>Trạng thái</span>
            <span></span>
          </div>
          {records.map((record) => (
            <div
              key={record.id}
              className={`tt-pending-row tt-pending-row--${record.status} ${record.status === 'done' ? 'tt-pending-row--clickable' : ''}`}
              onClick={() => record.status === 'done' && !loading && handleRowClick(record.id)}
              role={record.status === 'done' ? 'button' : undefined}
              tabIndex={record.status === 'done' ? 0 : undefined}
            >
              <span className="tt-pending-row__check" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(record.id)}
                  onChange={() => toggleOne(record.id)}
                />
              </span>
              {/* DB id = stable lifetime number for this record. Kept
                  instead of map-index so error rows don't renumber when
                  the operator deletes or reorders entries — "#142 lỗi"
                  stays referenceable across reloads. */}
              <span className="tt-pending-row__num">#{record.id}</span>
              <span className="tt-pending-row__name">
                <span className="tt-pending-row__name-text">
                  {record.hoTen || <em style={{ color: '#999' }}>Chưa detect</em>}
                </span>
                {record.soGPLX && (
                  <span className="tt-records__gplx-badge" title={`GPLX: ${record.soGPLX}`}>
                    <i className="material-icons">directions_car</i>
                    {record.hangGPLXMoi || record.hangGPLX || 'GPLX'}
                  </span>
                )}
              </span>
              <span className="tt-pending-row__cccd">
                {record.soCCCD || '---'}
              </span>
              <span className="tt-pending-row__files">
                <i className="material-icons">image</i> {record.fileCount || 0}
              </span>
              <span className="tt-pending-row__source">
                {/* Per-doc badges — each document's source is rendered
                    independently. NO record-level fallback: if
                    cccdSource is null, CCCD badge is omitted (instead
                    of inheriting record.scanMethod, which would lie
                    when the other doc was QR-hit). Two distinct badges
                    ("CCCD: QR" + "GPLX: OCR") display side-by-side when
                    the operator actually used different engines. */}
                {(() => {
                  const c = record.cccdSource ?? null;
                  const g = record.gplxSource ?? null;
                  const hasCccd = !!record.soCCCD;
                  const hasGplx = !!record.soGPLX;
                  const render = (src: 'qr' | 'ocr' | 'manual' | null | undefined, prefix?: string) => {
                    if (src === 'qr') return <span className="tt-source-badge tt-source-badge--qr" title="Đọc từ mã QR — chính xác 100%"><i className="material-icons">verified</i> {prefix ? `${prefix}: ` : ''}QR</span>;
                    if (src === 'ocr') return <span className="tt-source-badge tt-source-badge--ocr" title="Nhận dạng bằng AI — cần kiểm tra lại"><i className="material-icons">auto_awesome</i> {prefix ? `${prefix}: ` : ''}OCR</span>;
                    if (src === 'manual') return <span className="tt-source-badge tt-source-badge--manual" title="Nhập tay"><i className="material-icons">edit_note</i> {prefix ? `${prefix}: ` : ''}Tay</span>;
                    return null;
                  };
                  // Only 1 doc present → single badge (no prefix).
                  if (hasCccd && !hasGplx) return render(c);
                  if (!hasCccd && hasGplx) return render(g);
                  // Both docs present & sources match → single badge without prefix.
                  if (hasCccd && hasGplx && c && g && c === g) return render(c);
                  // Both docs present with different (or one missing) source →
                  // render each explicitly with prefix. Missing source → skip
                  // that badge (no lying fallback).
                  if (hasCccd && hasGplx) {
                    return (
                      <>
                        {render(c, 'CCCD')}
                        {render(g, 'GPLX')}
                      </>
                    );
                  }
                  // No docs at all → show record-level scanMethod (only useful
                  // fallback, e.g. for manual-entry records pre-OCR).
                  return render(record.scanMethod);
                })()}
              </span>
              <span className="tt-pending-row__status">
                {record.status === 'pending' && (
                  <span className="tt-status-icon--pending">
                    <i className="material-icons">schedule</i> Chờ
                  </span>
                )}
                {record.status === 'detecting' && (
                  <span className="tt-status-icon--detecting">
                    <span className="tt-spinner tt-spinner--sm" />
                    {record.currentStep || 'Đang xử lý...'}
                  </span>
                )}
                {record.status === 'done' && (
                  <span className="tt-status-icon--done">
                    <i className="material-icons">check_circle</i> Hoàn tất
                  </span>
                )}
                {record.status === 'error' && (
                  <span
                    className="tt-status-icon--error"
                    title={record.errorMsg || 'Unknown error'}
                  >
                    <i className="material-icons">error</i> {record.errorMsg || 'Lỗi'}
                  </span>
                )}
              </span>
              <span className="tt-pending-row__actions">
                {record.status === 'error' && (
                  <button
                    type="button"
                    className="tt-btn tt-btn--outline tt-btn--xs"
                    onClick={(e) => { e.stopPropagation(); onRetry(record.id); }}
                    title="Thử lại"
                  >
                    <i className="material-icons">refresh</i>
                  </button>
                )}
                {/* Detect lại — re-run OCR on stored images. Highlighted
                    yellow when the row clearly came back empty (CCCD +
                    name both blank), else neutral so it's still
                    discoverable when operator wants to retry a partial
                    detection. */}
                {(record.status === 'done' || record.status === 'error') && (() => {
                  const empty = !record.soCCCD && !record.hoTen && !record.soGPLX;
                  const busy = redetectingId === record.id;
                  return (
                    <button
                      type="button"
                      className={`tt-btn tt-btn--outline tt-btn--xs${empty ? ' tt-btn--warning' : ''}`}
                      onClick={(e) => { e.stopPropagation(); void handleRedetectClick(record.id); }}
                      disabled={busy || redetectingId !== null}
                      title={empty ? 'AI chưa đọc được — bấm để detect lại' : 'Detect lại từ ảnh đã lưu'}
                    >
                      {busy
                        ? <span className="tt-spinner tt-spinner--sm" />
                        : <i className="material-icons">auto_fix_high</i>}
                    </button>
                  );
                })()}
                <button
                  type="button"
                  className="tt-btn tt-btn--outline tt-btn--xs tt-btn--danger"
                  onClick={(e) => { e.stopPropagation(); handleRemoveClick(record.id); }}
                  title="Xóa khỏi hàng đợi"
                >
                  <i className="material-icons">delete</i>
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Inline transfer dialog — same-hang + open courses only */}
      {transferOpen && (
        <div className="tt-modal-overlay" onClick={() => !submittingTransfer && setTransferOpen(false)}>
          <div className="tt-modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="tt-modal__header">
              <h3>
                <i className="material-icons">swap_horiz</i>
                Chuyển {selectedIds.size} hồ sơ chờ lưu
              </h3>
              <button type="button" className="tt-modal__close" onClick={() => setTransferOpen(false)} disabled={submittingTransfer}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="tt-modal__body">
              <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
                Khóa đích phải <strong>đang mở</strong> và cùng <strong>hạng {currentCourseHang}</strong>.
              </p>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Khóa đích:
              </label>
              <select
                value={transferTarget ?? ''}
                onChange={e => setTransferTarget(e.target.value ? Number(e.target.value) : null)}
                disabled={submittingTransfer}
                style={{ width: '100%' }}
              >
                <option value="">— Chọn khóa đích —</option>
                {eligibleTargets.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} (Hạng {c.hang}, {c.recordCount ?? 0} hồ sơ)
                  </option>
                ))}
              </select>
            </div>
            <div className="tt-modal__footer">
              <button type="button" className="tt-btn tt-btn--outline" onClick={() => setTransferOpen(false)} disabled={submittingTransfer}>
                Hủy
              </button>
              <button
                type="button"
                className="tt-btn tt-btn--primary"
                onClick={handleTransferSubmit}
                disabled={!transferTarget || submittingTransfer}
              >
                {submittingTransfer ? 'Đang chuyển...' : 'Xác nhận chuyển'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingRecordsTab;
